/**
 * Sección "PRÓXIMAS ACCIONES" rediseñada — Sprint Pre-Demo Alcaldesa.
 *
 * Reemplaza el legacy ActionPlanSection (3 cards con CTA repetido) por
 * UNA narrativa con 3 bloques (urgencia + vecino + sector con margen) y
 * UN CTA al final. Diseño aprobado en brainstorm 27-may: strategic
 * briefing + FOMO.
 *
 * Pulido visual (28-may, post-feedback del founder): número grande
 * como protagonista, urgency badge colored, grid 2 col para vecinos +
 * crecer, iconos lucide, pills colored para scores (reuso del lenguaje
 * visual del ScoreHero y de la card de proyectos SGR). Recomendaciones
 * basadas en `ui-ux-pro-max` para producto B2G (Accessible & Ethical
 * WCAG AAA — high contrast, sin ornamentos, focus rings visibles).
 *
 * Renderiza `null` si `has_content=false` (empty state silencioso —
 * NO mostramos "no hay actividad", según la regla del founder).
 */

import { ArrowRight, Clock, TrendingUp, Users } from "lucide-react";
import { useEffect } from "react";
import { Link } from "react-router-dom";

import { track } from "@/lib/analytics";
import type { NextActionsBriefingDTO } from "@/lib/api";

import { bigIntFromCop, formatCop } from "./_shared";

/** Color map para pills de sector — espejo del usado en ProjectsSgrSection. */
const SECTOR_COLOR_MAP: Record<string, { bg: string; text: string }> = {
  VIVIENDA: { bg: "bg-blue-100", text: "text-blue-800" },
  AGRICULTURA: { bg: "bg-amber-100", text: "text-amber-800" },
  EDUCACION: { bg: "bg-violet-100", text: "text-violet-800" },
  EDUCACIÓN: { bg: "bg-violet-100", text: "text-violet-800" },
  SALUD: { bg: "bg-pink-100", text: "text-pink-800" },
  VIAS: { bg: "bg-slate-100", text: "text-slate-800" },
  ENERGIA: { bg: "bg-yellow-100", text: "text-yellow-800" },
  ENERGÍA: { bg: "bg-yellow-100", text: "text-yellow-800" },
  AGUA: { bg: "bg-cyan-100", text: "text-cyan-800" },
  AMBIENTE: { bg: "bg-emerald-100", text: "text-emerald-800" },
  TRANSPORTE: { bg: "bg-slate-100", text: "text-slate-800" },
  INCLUSIÓN: { bg: "bg-rose-100", text: "text-rose-800" },
  INCLUSION: { bg: "bg-rose-100", text: "text-rose-800" },
  DEPORTE: { bg: "bg-orange-100", text: "text-orange-800" },
  CULTURA: { bg: "bg-fuchsia-100", text: "text-fuchsia-800" },
};

function sectorPillColors(sector: string): { bg: string; text: string } {
  const key = (sector.split(/[, ]/)[0] ?? "").toUpperCase();
  return SECTOR_COLOR_MAP[key] ?? { bg: "bg-slate-100", text: "text-slate-700" };
}

function capitalizeFirst(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

/** Pill colored para un sector. Capitaliza la primera letra. */
function SectorPill({ sector }: { sector: string }) {
  const colors = sectorPillColors(sector);
  return (
    <span
      className={`inline-block rounded-full ${colors.bg} px-2 py-0.5 text-[11px] font-semibold ${colors.text}`}
    >
      {capitalizeFirst(sector)}
    </span>
  );
}

/** Pill colored para un score sectorial. Espeja `scoreColorClass` del Hero. */
function ScorePill({ value }: { value: number }) {
  let bg = "bg-red-100";
  let text = "text-red-700";
  if (value >= 1.1) {
    bg = "bg-emerald-100";
    text = "text-emerald-700";
  } else if (value >= 0.9) {
    bg = "bg-amber-100";
    text = "text-amber-800";
  }
  return (
    <span
      className={`inline-flex items-center rounded-full ${bg} px-2 py-0.5 text-[11px] font-semibold ${text}`}
      data-testid="briefing-score-pill"
    >
      {value.toFixed(2)}×
    </span>
  );
}

/** Badge de urgencia coloreado según días al deadline más próximo. */
function UrgencyBadge({ days }: { days: number | null }) {
  if (days === null) return null;
  let tone: "danger" | "warning" | "neutral" = "neutral";
  if (days <= 2) tone = "danger";
  else if (days <= 7) tone = "warning";

  const toneClasses = {
    danger: "bg-red-50 text-red-700 ring-red-200",
    warning: "bg-amber-50 text-amber-800 ring-amber-200",
    neutral: "bg-slate-50 text-slate-700 ring-slate-200",
  }[tone];

  const label =
    days <= 0 ? "Cierra hoy" : `La más urgente cierra en ${days} día${days === 1 ? "" : "s"}`;

  return (
    <span
      className={`mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset ${toneClasses}`}
      data-testid="briefing-urgency-badge"
    >
      <Clock className="h-3 w-3" aria-hidden="true" />
      {label}
    </span>
  );
}

export function NextActionsBriefing({ data }: { data: NextActionsBriefingDTO | null }) {
  // Telemetría: render efectivo o oculto. 1 fire por cambio de payload.
  useEffect(() => {
    if (!data) return;
    if (!data.has_content) {
      track({
        event_name: "municipality_next_actions_hidden",
        properties: {
          dane_code: data.dane_code,
          reason: hiddenReason(data),
        },
      });
      return;
    }
    track({
      event_name: "municipality_next_actions_viewed",
      properties: {
        dane_code: data.dane_code,
        opportunities_count: data.active_opportunities?.count ?? 0,
        has_neighbor: data.neighbor_movement !== null,
        has_weak_sector: data.weak_sector !== null,
        urgent_deadline_days: data.active_opportunities?.next_deadline_days ?? null,
      },
    });
  }, [data]);

  if (!data || !data.has_content) return null;

  const handleCtaClick = () => {
    track({
      event_name: "municipality_next_actions_cta_clicked",
      properties: {
        dane_code: data.dane_code,
        opportunities_count: data.active_opportunities?.count ?? 0,
      },
    });
  };

  // Link interno (/...) → Link de react-router. Externo → <a>.
  const ctaIsInternal = data.cta_href.startsWith("/");
  // Removemos el "→" del label porque ahora va el icono ArrowRight.
  const ctaCleanLabel = data.cta_label.replace(/\s*→\s*$/, "");
  const ctaClassName =
    "group inline-flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white shadow-xs transition-all hover:bg-brand-700 hover:shadow-md focus:outline-hidden focus:ring-2 focus:ring-brand-500 focus:ring-offset-2";

  return (
    <section
      data-testid="ficha-next-actions"
      aria-labelledby="next-actions-heading"
      className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xs"
    >
      {/* Header con accent dot azul */}
      <header className="flex items-center justify-between gap-2 border-b border-slate-100 pb-3">
        <div className="flex items-center gap-2">
          <span aria-hidden="true" className="inline-block h-2 w-2 rounded-full bg-brand-600" />
          <p
            id="next-actions-heading"
            className="text-xs font-semibold tracking-wider text-slate-700 uppercase"
          >
            Próximas acciones
          </p>
        </div>
        <p className="text-xs text-slate-400">{data.briefing_week_label}</p>
      </header>

      {/* Bloque 1 — Headline urgencia con número grande */}
      {data.active_opportunities && (
        <div className="mt-5" data-testid="briefing-active-opps">
          <div className="flex items-end gap-3">
            <span
              className="text-5xl leading-none font-extrabold tracking-tight text-brand-700"
              data-testid="briefing-active-opps-count"
            >
              {data.active_opportunities.count}
            </span>
            <p className="pb-1.5 text-sm font-medium text-slate-900">
              oportunidades activas que aplican
            </p>
          </div>
          <UrgencyBadge days={data.active_opportunities.next_deadline_days} />
        </div>
      )}

      {/* Bloques en grid responsivo */}
      {(data.neighbor_movement || data.weak_sector) && (
        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          {/* Bloque 2 — Vecinos en movimiento */}
          {data.neighbor_movement && (
            <div
              className="rounded-xl border border-slate-200 bg-linear-to-br from-slate-50 to-white p-4"
              data-testid="briefing-neighbor"
            >
              <div className="flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5 text-slate-600" aria-hidden="true" />
                <p className="text-[11px] font-semibold tracking-wider text-slate-700 uppercase">
                  {data.neighbor_movement.is_geographic_neighbor
                    ? "Vecinos en movimiento"
                    : "Municipios similares en movimiento"}
                </p>
              </div>
              <p className="mt-2 text-sm leading-snug text-slate-900">
                <span className="font-semibold">
                  {capitalizeFirst(data.neighbor_movement.neighbor_name)}
                </span>{" "}
                aprobó{" "}
                <span className="font-semibold">
                  {formatCop(bigIntFromCop(data.neighbor_movement.amount_cop))}
                </span>
                {data.neighbor_movement.vigencia ? ` (${data.neighbor_movement.vigencia})` : ""}
              </p>
              {/* Sin historial SGR del que aprobó el sector llega vacío (y el
                  score, null): no hay nada que pintar en esta fila. */}
              {data.neighbor_movement.sector && (
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <SectorPill sector={data.neighbor_movement.sector} />
                  {data.neighbor_movement.muni_sector_score !== null && (
                    <>
                      <span className="text-[11px] text-slate-400">en tu</span>
                      <ScorePill value={data.neighbor_movement.muni_sector_score} />
                    </>
                  )}
                </div>
              )}
              {data.neighbor_movement.muni_sector_score !== null &&
                data.neighbor_movement.sector_alignment !== "neutral" && (
                  <p className="mt-2 text-[11px] text-slate-500">
                    {data.neighbor_movement.sector_alignment === "strong"
                      ? "Tu desempeño en este sector es fuerte"
                      : "Tu desempeño en este sector hoy es bajo"}
                  </p>
                )}
            </div>
          )}

          {/* Bloque 3 — Dónde puedes crecer */}
          {data.weak_sector && (
            <div
              className="rounded-xl border border-slate-200 bg-linear-to-br from-slate-50 to-white p-4"
              data-testid="briefing-weak-sector"
            >
              <div className="flex items-center gap-1.5">
                <TrendingUp className="h-3.5 w-3.5 text-slate-600" aria-hidden="true" />
                <p className="text-[11px] font-semibold tracking-wider text-slate-700 uppercase">
                  Dónde puedes crecer
                </p>
              </div>
              <p className="mt-2 text-sm leading-snug text-slate-900">
                Tu sector con mayor brecha vs peers:
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <SectorPill sector={data.weak_sector.sector} />
                <ScorePill value={data.weak_sector.score} />
              </div>
              <p className="mt-2 text-[11px] text-slate-500">
                <span className="font-semibold text-brand-700">
                  {data.weak_sector.active_opportunities_count}
                </span>{" "}
                convocatoria
                {data.weak_sector.active_opportunities_count === 1 ? "" : "s"} activa
                {data.weak_sector.active_opportunities_count === 1 ? "" : "s"} en este sector
              </p>
            </div>
          )}
        </div>
      )}

      {/* CTA único al final */}
      <div className="mt-6 flex items-center justify-end border-t border-slate-100 pt-4">
        {ctaIsInternal ? (
          <Link
            to={data.cta_href}
            data-testid="briefing-cta"
            onClick={handleCtaClick}
            className={ctaClassName}
          >
            {ctaCleanLabel}
            <ArrowRight
              className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
              aria-hidden="true"
            />
          </Link>
        ) : (
          <a
            href={data.cta_href}
            data-testid="briefing-cta"
            onClick={handleCtaClick}
            className={ctaClassName}
          >
            {ctaCleanLabel}
            <ArrowRight
              className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
              aria-hidden="true"
            />
          </a>
        )}
      </div>
    </section>
  );
}

function hiddenReason(data: NextActionsBriefingDTO): string {
  if (!data.active_opportunities && !data.neighbor_movement && !data.weak_sector) {
    return "all_empty";
  }
  // Defensivo: has_content=false pero hay algún bloque (no debería pasar).
  return "unknown";
}
